import { KeyboardEvent, MouseEvent, useState } from 'react'

import type { Cursor, SelectionMode } from '../context/DataGridContext'
import { buildVirtualGrid, VirtualCell } from '../context/virtualGrid'
import { Cell, Id } from '../types'
import { toCellId } from '../utils'

export const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'] as const
export type ArrowKey = (typeof arrowKeys)[number]

export function isArrowKey(key: string): key is ArrowKey {
    return (arrowKeys as readonly string[]).includes(key)
}

export type NavigationOptions = {
    rowIds: readonly Id[]
    colIds: readonly Id[]
    selectionMode?: SelectionMode
    allowDeselect?: boolean
    selectedCells?: Cell[]
    onSelectionChange?: (cells: Cell[]) => void
}

const toCell = (cell: VirtualCell): Cell => ({ rowId: String(cell.rowId), colId: String(cell.colId) })

const resolveCursor = (cursorState: Cursor, selectedCells: Cell[]): Cursor => {
    if (selectedCells.length === 0) return undefined
    if (cursorState === undefined) return selectedCells[0]

    const cursorId = toCellId(cursorState)
    const hasCursorInSelection = selectedCells.some(cell => toCellId(cell) === cursorId)
    return hasCursorInSelection ? cursorState : selectedCells[0]
}

const EMPTY_CELLS: Cell[] = []

export function useGridNavigation({
    rowIds,
    colIds,
    selectionMode = 'none',
    allowDeselect = true,
    selectedCells: selectedCellsProp,
    onSelectionChange,
}: NavigationOptions) {
    const isSelectionControlled = selectedCellsProp !== undefined && onSelectionChange !== undefined
    const selectedCells = selectedCellsProp ?? EMPTY_CELLS
    const effectiveSelectionMode: SelectionMode = isSelectionControlled ? selectionMode : 'none'

    const [cursorState, setCursor] = useState<Cursor>(selectedCells[0])
    // Shift-selection keeps its origin while the cursor moves.
    const [extendingAnchorState, setExtendingAnchor] = useState<Cursor>(cursorState)

    const { move, jump, getVirtualCell, getRect } = buildVirtualGrid(rowIds, colIds)

    const virtualSelectedCells = selectedCells.map(cell => getVirtualCell(cell.rowId, cell.colId)).filter((cell): cell is VirtualCell => cell !== undefined)

    const cursor = isSelectionControlled ? resolveCursor(cursorState, selectedCells) : undefined
    const extendingAnchor = isSelectionControlled ? resolveCursor(extendingAnchorState, selectedCells) : undefined

    const emitSelectionChange = (cells: Cell[]) => {
        if (!isSelectionControlled) return
        onSelectionChange(cells)
    }

    const setSelection = (cells: Cell[]) => {
        if (!isSelectionControlled) return
        setCursor(cells[0])
        setExtendingAnchor(cells[0])
        emitSelectionChange(cells)
    }

    const setDefault = () => {
        if (selectedCells.length === 0) return
        setCursor(undefined)
        setExtendingAnchor(undefined)
        emitSelectionChange([])
    }

    const computeNextSelection = (currentSelection: Cell[], cell: Cell, isMultiMode: boolean): Cell[] => {
        if (!isMultiMode && !allowDeselect) return [cell]

        const id = toCellId(cell)
        const exists = currentSelection.some(selectedCell => toCellId(selectedCell) === id)

        if (!isMultiMode) {
            return exists ? [] : [cell]
        }

        if (!allowDeselect) {
            return exists ? currentSelection : [...currentSelection, cell]
        }

        return exists ? currentSelection.filter(selectedCell => toCellId(selectedCell) !== id) : [...currentSelection, cell]
    }

    const onMouseUp = (event: MouseEvent, rowId: Id, colId: Id) => {
        if (effectiveSelectionMode === 'none') return

        const isMeta = event.metaKey || event.ctrlKey
        const isShift = event.shiftKey
        const isMultipleSelection = effectiveSelectionMode === 'multiple'

        const isExtending = isMultipleSelection && isShift
        const clicked: Cell = { rowId: String(rowId), colId: String(colId) }

        setCursor(clicked)
        if (!isExtending || !extendingAnchor) {
            setExtendingAnchor(clicked)
        }

        if (!isMultipleSelection) {
            emitSelectionChange(computeNextSelection(selectedCells, clicked, false))
            return
        }

        if (isShift) {
            const anchor = extendingAnchor ?? clicked
            const virtualAnchorCell = getVirtualCell(anchor.rowId, anchor.colId)
            const virtualClickedCell = getVirtualCell(clicked.rowId, clicked.colId)

            if (!virtualAnchorCell || !virtualClickedCell) {
                emitSelectionChange(computeNextSelection(selectedCells, clicked, true))
                return
            }

            emitSelectionChange(getRect([virtualAnchorCell, virtualClickedCell]).map(toCell))
            return
        }

        emitSelectionChange(computeNextSelection(selectedCells, clicked, isMeta))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (effectiveSelectionMode === 'none') return
        if (!cursor) return

        const { key, metaKey, ctrlKey, shiftKey } = event
        if (!isArrowKey(key)) return

        event.preventDefault()

        const shouldJump = metaKey || ctrlKey
        const isExtending = shiftKey && effectiveSelectionMode === 'multiple'

        const virtualCursor = getVirtualCell(cursor.rowId, cursor.colId)

        if (!virtualCursor) return

        const keyActions: Record<ArrowKey, () => VirtualCell | undefined> = {
            ArrowUp: () => move(virtualCursor, 'up'),
            ArrowDown: () => move(virtualCursor, 'down'),
            ArrowLeft: () => (shouldJump ? jump(virtualCursor, 'left') : move(virtualCursor, 'left')),
            ArrowRight: () => (shouldJump ? jump(virtualCursor, 'right') : move(virtualCursor, 'right')),
        }

        const nextVirtualCell = keyActions[key]()

        if (!nextVirtualCell) return

        const nextCell = toCell(nextVirtualCell)
        setCursor(nextCell)

        if (!isExtending) {
            setExtendingAnchor(nextCell)
            emitSelectionChange([nextCell])
            return
        }

        const anchor = extendingAnchor ?? cursor
        const virtualAnchor = getVirtualCell(anchor.rowId, anchor.colId)

        if (!virtualAnchor) {
            emitSelectionChange(computeNextSelection(selectedCells, nextCell, true))
            return
        }

        emitSelectionChange(getRect([virtualAnchor, nextVirtualCell]).map(toCell))
    }

    return {
        cursor,
        selectedCells,
        virtualSelectedCells,
        setDefault,
        onMouseUp,
        onKeyDown,
        getVirtualCell,
        setSelection,
    } as const
}
